/**
 * useOptimisticData Hook
 * 
 * Generic hook for optimistic data loading with caching:
 * - Returns cached data immediately if available
 * - Fetches fresh data in background
 * - Updates UI when fresh data arrives
 * - Handles loading, error, and refresh states
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { cacheManager } from '../services/cacheManager';

interface UseOptimisticDataOptions {
  ttl?: number; // Cache TTL in milliseconds
  enabled?: boolean; // Whether to fetch data (default: true)
  onSuccess?: (data: any) => void; // Callback on successful fetch
  onError?: (error: Error) => void; // Callback on error
}

interface UseOptimisticDataReturn<T> {
  data: T | null;
  isLoading: boolean; // True only on first load with no cache
  isRefreshing: boolean; // True when fetching in background
  error: Error | null;
  refresh: () => Promise<void>; // Force refresh
}

/**
 * Hook for optimistic data loading with caching
 * 
 * @param fetchFn - Function that fetches the data
 * @param cacheKey - Unique key for caching this data
 * @param options - Optional configuration
 * @returns Data, loading states, error, and refresh function
 */
export function useOptimisticData<T>(
  fetchFn: () => Promise<T>,
  cacheKey: string,
  options: UseOptimisticDataOptions = {}
): UseOptimisticDataReturn<T> {
  const {
    ttl = 5 * 60 * 1000, // Default 5 minutes
    enabled = true,
    onSuccess,
    onError,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Track if this is the first load
  const isFirstLoad = useRef(true);
  
  // Track if component is mounted (prevent state updates after unmount)
  const isMounted = useRef(true);

  const fetchData = useCallback(
    async (isBackground = false) => {
      // Check cache first
      const cached = cacheManager.get<T>(cacheKey);
      
      if (cached) {
        if (isMounted.current) {
          setData(cached);
          setIsLoading(false);
          isFirstLoad.current = false;
        }
        
        // If this is a background fetch, set refreshing state
        if (isBackground && isMounted.current) {
          setIsRefreshing(true);
        }
      } else {
        // No cache - show loading state
        if (isMounted.current && !isBackground) {
          setIsLoading(true);
        }
      }

      try {
        // Use deduplication to prevent multiple simultaneous requests
        const freshData = await cacheManager.deduplicate(
          `fetch_${cacheKey}`,
          fetchFn
        );

        // Store in cache
        cacheManager.set(cacheKey, freshData, ttl);

        if (isMounted.current) {
          setData(freshData);
          setError(null);
          setIsLoading(false);
          setIsRefreshing(false);
          isFirstLoad.current = false;
        }

        // Call success callback
        if (onSuccess) {
          onSuccess(freshData);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch data');
        console.error(`❌ [useOptimisticData] Error fetching ${cacheKey}:`, error);

        if (isMounted.current) {
          setError(error);
          setIsLoading(false);
          setIsRefreshing(false);
        }

        // Call error callback
        if (onError) {
          onError(error);
        }
      }
    },
    [fetchFn, cacheKey, ttl, onSuccess, onError]
  );

  // Initial fetch
  useEffect(() => {
    if (!enabled) {
      return;
    }

    fetchData(false);
  }, [enabled, fetchData]);

  // Cleanup
  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Refresh function (force fetch, bypass cache)
  const refresh = useCallback(async () => {
    // Invalidate cache
    cacheManager.invalidate(cacheKey);
    
    // Fetch fresh data
    await fetchData(false);
  }, [cacheKey, fetchData]);

  return {
    data,
    isLoading: isLoading && isFirstLoad.current, // Only show loading on first load
    isRefreshing,
    error,
    refresh,
  };
}

